import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Check, ChevronLeft, Film, Loader2, Plus, Search, Trash2, Users, Pencil, X } from "lucide-react";
import { api, CATEGORY_NAME } from "@/lib/api";
import { ClipCardWithActions } from "@/components/ClipCard";
import ClipUploader from "@/components/ClipUploader";

export default function Events() {
  const { eventName } = useParams();
  if (eventName) return <EventDetail eventName={decodeURIComponent(eventName)} />;
  return <EventsIndex />;
}

function EventsIndex() {
  const [events, setEvents] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/library-events")
      .then((r) => setEvents(Array.isArray(r.data) ? r.data : []))
      .catch((e) => setError(e?.response?.data?.detail || e.message));
  }, []);

  const filtered = useMemo(() => {
    if (!events) return [];
    const s = q.trim().toLowerCase();
    if (!s) return events;
    return events.filter((ev) => {
      const hay = [
        ev.event_name,
        (ev.categories || []).map((c) => CATEGORY_NAME[c] || c).join(" "),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(s);
    });
  }, [events, q]);

  if (error) {
    return (
      <div className="p-8">
        <div className="text-rose-400 text-sm">Failed to load events: {error}</div>
      </div>
    );
  }
  if (events === null) {
    return (
      <div className="p-8 flex items-center gap-2 text-stone-400">
        <Loader2 size={16} className="animate-spin" /> Loading events…
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl">
      <div className="label-tiny mb-2">Performances</div>
      <h1 className="font-display uppercase text-5xl font-bold leading-[0.95] mb-1">Performances</h1>
      <p className="text-stone-400 text-sm mb-6">
        Every performance across your cuts and clips. Tag your cuts with a
        Performance Name in Studio to see them grouped here.
      </p>
      {events.length > 0 && (
        <div className="mb-6 max-w-md">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 pointer-events-none" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search performance name, discipline…"
              className="field-input w-full !pl-9"
              data-testid="performances-index-search"
            />
          </div>
        </div>
      )}
      {events.length === 0 ? (
        <div className="border border-dashed border-stone-700 p-8 text-center text-stone-500">
          No performances yet. Open a cut in Studio and set a Performance Name — it will show up here.
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-stone-800 p-8 text-center text-stone-500">
          No performances match "{q}"
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="event-grid">
          {filtered.map((ev) => (
            <Link
              key={ev.event_name}
              to={`/performances/${encodeURIComponent(ev.event_name)}`}
              className="border border-stone-800 hover:border-orange-700 transition-colors p-4 block group"
              data-testid={`event-card-${ev.event_name}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <div className="font-display uppercase text-lg font-bold leading-tight truncate">{ev.event_name}</div>
                  {ev.latest_created && (
                    <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-0.5 inline-flex items-center gap-1">
                      <CalendarDays size={10} /> {new Date(ev.latest_created).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-stone-400 mb-2">
                <span className="inline-flex items-center gap-1"><Film size={11} className="text-orange-500" /> {ev.clip_count} clips</span>
                <span className="text-stone-700">·</span>
                <span>{ev.cut_count} cuts</span>
              </div>
              {ev.categories?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {ev.categories.slice(0, 4).map((c) => (
                    <span key={c} className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-stone-800 text-stone-300">
                      {CATEGORY_NAME[c] || c}
                    </span>
                  ))}
                  {ev.categories.length > 4 && (
                    <span className="text-[9px] text-stone-500">+{ev.categories.length - 4}</span>
                  )}
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function EventDetail({ eventName }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [showAdmins, setShowAdmins] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [showAddClips, setShowAddClips] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const load = () => {
    api.get(`/library-events/${encodeURIComponent(eventName)}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || e.message));
  };
  useEffect(load, [eventName]);

  const parentAssociation = data?.association || "";
  const backHref = parentAssociation ? `/associations/${encodeURIComponent(parentAssociation)}` : "/associations";
  const backLabel = parentAssociation ? `Back to ${parentAssociation}` : "Back to Associations";

  // Client-side search across the rendered clips
  const filteredClips = useMemo(() => {
    const clips = data?.clips || [];
    const q = search.trim().toLowerCase();
    if (!q) return clips;
    return clips.filter((c) => {
      const hay = [
        c.rider_horse_name, c.heeler_name, c.horse_name, c.owner_name,
        c.sub_label, c.notes, c.source_video_name,
        CATEGORY_NAME[c.category] || c.category,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [data, search]);

  if (error) {
    return (
      <div className="p-8">
        <button onClick={() => navigate(backHref)} className="text-stone-400 hover:text-orange-400 inline-flex items-center gap-1 text-sm mb-4">
          <ChevronLeft size={14} /> {backLabel}
        </button>
        <div className="text-rose-400 text-sm">{error}</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="p-8 flex items-center gap-2 text-stone-400">
        <Loader2 size={16} className="animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl">
      <button onClick={() => navigate(backHref)} className="text-stone-400 hover:text-orange-400 inline-flex items-center gap-1 text-sm mb-4" data-testid="events-back">
        <ChevronLeft size={14} /> {backLabel}
      </button>
      {/* Header stacks on narrow screens so long event titles never get
          clipped by the action buttons. On desktop the buttons sit to the
          right — but the title is allowed to wrap freely (break-words). */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-6 mb-8">
        <div className="min-w-0 flex-1">
          <div className="label-tiny mb-2">Performance</div>
          <div className="flex items-start gap-2 flex-wrap">
            <h1
              className="font-display uppercase text-4xl sm:text-5xl font-bold leading-[0.95] break-words min-w-0"
              data-testid="event-title"
            >
              {eventName}
            </h1>
            <button
              onClick={() => setShowRename(true)}
              title="Rename this performance"
              className="mt-2 p-1.5 border border-stone-700 hover:border-orange-500 text-stone-400 hover:text-orange-400"
              data-testid="event-rename-btn"
            >
              <Pencil size={12} />
            </button>
          </div>
          <div className="text-sm text-stone-400 mt-2">
            {data.clips.length} clips · {data.cuts.length} cuts
          </div>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0 self-start">
          <button
            onClick={() => setShowAddClips(true)}
            className="btn-primary inline-flex items-center gap-1.5"
            data-testid="event-add-clips-btn"
          >
            <Plus size={12} /> Add clips
          </button>
          <button
            onClick={() => setShowAdmins(true)}
            className="btn-secondary inline-flex items-center gap-1.5"
            data-testid="event-manage-admins"
          >
            <Users size={12} /> Manage Admins
          </button>
        </div>
      </div>

      {data.clips.length === 0 ? (
        <div className="border border-dashed border-stone-700 p-8 text-center text-stone-500">
          <div className="mb-3">No rendered clips yet for this performance. Cuts:  {data.cuts.length}.</div>
          <button
            onClick={() => setShowAddClips(true)}
            className="btn-primary inline-flex items-center gap-1.5"
            data-testid="event-add-clips-empty"
          >
            <Plus size={12} /> Add clips from Library
          </button>
        </div>
      ) : (
        <>
          <div className="mb-5 max-w-md">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search rider, horse, owner, notes…"
                className="field-input w-full !pl-9"
                data-testid="performance-clip-search"
              />
            </div>
            {search && (
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-1.5">
                {filteredClips.length} of {data.clips.length} match "{search}"
              </div>
            )}
          </div>

          {filteredClips.length === 0 ? (
            <div className="border border-dashed border-stone-800 p-8 text-center text-stone-500">
              No clips match that search.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="event-clip-grid">
              {filteredClips.map((clip) => (
                <ClipCardWithActions key={clip.id} clip={clip} onChanged={load} />
              ))}
            </div>
          )}
        </>
      )}

      {showAdmins && (
        <EventAdminsModal eventName={eventName} onClose={() => setShowAdmins(false)} />
      )}
      {showRename && (
        <EventRenameModal
          eventName={eventName}
          onClose={() => setShowRename(false)}
          onRenamed={(newName) => {
            setShowRename(false);
            navigate(`/performances/${encodeURIComponent(newName)}`, { replace: true });
          }}
        />
      )}
      {showAddClips && (
        <AddClipsToPerformanceModal
          performanceName={eventName}
          association={data.association || parentAssociation}
          existingClipIds={new Set(data.clips.map((c) => c.id))}
          onClose={() => setShowAddClips(false)}
          onSaved={() => { setShowAddClips(false); load(); }}
        />
      )}
    </div>
  );
}


function AddClipsToPerformanceModal({ performanceName, association, existingClipIds, onClose, onSaved }) {
  const [tab, setTab] = useState("library"); // "library" | "upload"
  const [allClips, setAllClips] = useState(null);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [hideAlreadyIn, setHideAlreadyIn] = useState(true);

  useEffect(() => {
    api.get("/clips", { params: { limit: 2000 } })
      .then((r) => setAllClips(Array.isArray(r.data) ? r.data : []))
      .catch((e) => toast.error("Failed to load clips: " + (e?.response?.data?.detail || e.message)));
  }, []);

  // Esc closes
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const visible = useMemo(() => {
    let rows = allClips || [];
    if (hideAlreadyIn) rows = rows.filter((c) => !existingClipIds.has(c.id));
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((c) => {
      const hay = [
        c.rider_horse_name, c.heeler_name, c.horse_name, c.owner_name,
        c.sub_label, c.notes, c.source_video_name,
        c.association, c.event_name, c.performance_name,
        CATEGORY_NAME[c.category] || c.category,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(s);
    });
  }, [allClips, q, hideAlreadyIn, existingClipIds]);

  const toggle = (id) => {
    setPicked((p) => {
      const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n;
    });
  };

  const addAll = () => setPicked(new Set(visible.map((c) => c.id)));
  const clearAll = () => setPicked(new Set());

  const save = async () => {
    if (picked.size === 0) { onClose(); return; }
    setSaving(true);
    try {
      // PATCH each selected clip so `event_name` (legacy) + `performance_name`
      // both point at this performance, and its `association` gets attached.
      // Backend groups performances by these fields so the newly-added clips
      // start showing up under this performance immediately.
      const payload = {
        event_name: performanceName,
        performance_name: performanceName,
        ...(association ? { association } : {}),
      };
      let ok = 0, fail = 0;
      for (const cid of Array.from(picked)) {
        try {
          await api.patch(`/clips/${cid}`, payload);
          ok += 1;
        } catch (e) {
          fail += 1;
        }
      }
      if (ok) toast.success(`Added ${ok} clip${ok === 1 ? "" : "s"} to ${performanceName}`);
      if (fail) toast.error(`${fail} clip${fail === 1 ? "" : "s"} failed to update`);
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="add-clips-modal"
    >
      <div
        className="bg-stone-950 border border-orange-700 max-w-5xl w-full max-h-[92vh] flex flex-col relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-2 right-2 z-10 bg-black/70 hover:bg-black text-white rounded-full p-1.5 border border-stone-700"
          data-testid="add-clips-close"
          aria-label="Close"
        >
          <X size={14} />
        </button>
        <div className="px-5 py-4 border-b border-stone-800 pr-14 shrink-0">
          <div className="label-tiny">Add clips to performance</div>
          <div className="font-display uppercase text-xl truncate">{performanceName}</div>
          {association && <div className="text-[11px] text-stone-500 mt-0.5">Under {association}</div>}
          {/* Tabs */}
          <div className="flex gap-2 mt-3">
            <button
              type="button"
              onClick={() => setTab("library")}
              className={`btn-secondary !py-1 text-xs ${tab === "library" ? "!text-orange-500 !border-orange-700" : ""}`}
              data-testid="add-clips-tab-library"
            >
              From Library
            </button>
            <button
              type="button"
              onClick={() => setTab("upload")}
              className={`btn-secondary !py-1 text-xs ${tab === "upload" ? "!text-orange-500 !border-orange-700" : ""}`}
              data-testid="add-clips-tab-upload"
            >
              Upload New Videos
            </button>
          </div>
        </div>

        {tab === "upload" ? (
          <div className="flex-1 min-h-0 overflow-y-auto">
            <ClipUploader
              association={association}
              performanceName={performanceName}
              onClose={onClose}
              onUploaded={() => { onSaved(); }}
              embedded
            />
          </div>
        ) : (
        <>
        <div className="px-5 py-3 border-b border-stone-800 shrink-0 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 pointer-events-none" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rider, horse, event, sub-label…"
              className="field-input w-full !pl-9"
              data-testid="add-clips-search"
              autoFocus
            />
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-stone-400 select-none cursor-pointer">
            <input
              type="checkbox"
              checked={hideAlreadyIn}
              onChange={(e) => setHideAlreadyIn(e.target.checked)}
              className="accent-orange-500"
            />
            Hide clips already in this performance
          </label>
          <div className="text-[10px] uppercase tracking-widest text-stone-500 ml-auto">
            {picked.size} selected · {visible.length} visible
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto p-5">
          {allClips === null ? (
            <div className="text-stone-500 text-sm flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Loading clips…</div>
          ) : visible.length === 0 ? (
            <div className="border border-dashed border-stone-800 p-12 text-center text-stone-500">
              {q ? `No clips match "${q}"` : "No clips available to add."}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {visible.map((c) => {
                const on = picked.has(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggle(c.id)}
                    className={`text-left border transition-colors ${on ? "border-orange-600" : "border-stone-800 hover:border-stone-700"}`}
                    data-testid={`add-clip-${c.id}`}
                  >
                    <div className="aspect-video bg-black relative overflow-hidden">
                      {c.stream_url ? (
                        <video src={c.stream_url} muted preload="metadata" playsInline className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-stone-700"><Film size={20} /></div>
                      )}
                      <div className="absolute top-1.5 left-1.5">
                        <span className="tag-cat backdrop-blur-sm bg-stone-900/80 text-[9px]">{CATEGORY_NAME[c.category] || c.category}</span>
                      </div>
                      {on && (
                        <div className="absolute inset-0 bg-orange-600/25 flex items-center justify-center">
                          <div className="bg-orange-600 text-white rounded-full p-1.5"><Check size={13} /></div>
                        </div>
                      )}
                    </div>
                    <div className="p-2">
                      <div className="text-xs font-medium truncate" title={c.rider_horse_name || c.sub_label}>
                        {c.rider_horse_name || c.sub_label || "Untitled"}
                      </div>
                      <div className="text-[10px] text-stone-500 truncate">
                        {c.event_name || c.performance_name || c.association || "—"}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="px-5 py-4 border-t border-stone-800 flex flex-wrap gap-2 shrink-0">
          <button onClick={addAll} className="btn-secondary text-xs" data-testid="add-clips-select-all" disabled={!allClips || visible.length === 0}>
            Select all visible ({visible.length})
          </button>
          <button onClick={clearAll} className="btn-secondary text-xs" data-testid="add-clips-clear" disabled={picked.size === 0}>
            Clear selection
          </button>
          <div className="ml-auto flex gap-2">
            <button onClick={onClose} className="btn-secondary" data-testid="add-clips-cancel">Cancel</button>
            <button
              onClick={save}
              disabled={saving || picked.size === 0}
              className="btn-primary inline-flex items-center gap-1.5"
              data-testid="add-clips-save"
            >
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
              Add {picked.size || ""} clip{picked.size === 1 ? "" : "s"}
            </button>
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
}

function EventRenameModal({ eventName, onClose, onRenamed }) {
  const [newName, setNewName] = useState(eventName);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const v = newName.trim();
    if (!v || v === eventName) { onClose(); return; }
    setSaving(true);
    try {
      const r = await api.put(`/library-events/${encodeURIComponent(eventName)}/rename`, { new_name: v });
      const bits = [];
      if (r.data?.cuts_renamed) bits.push(`${r.data.cuts_renamed} cuts`);
      if (r.data?.clips_renamed) bits.push(`${r.data.clips_renamed} clips`);
      toast.success(`Renamed${bits.length ? " · " + bits.join(", ") : ""}`);
      onRenamed(v);
    } catch (e) {
      toast.error("Rename failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="event-rename-modal"
    >
      <div className="bg-stone-950 border border-orange-700 max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="label-tiny mb-1">Rename Performance</div>
            <h2 className="font-display uppercase text-2xl font-bold leading-tight break-words">{eventName}</h2>
            <p className="text-xs text-stone-500 mt-1">
              This updates every cut, clip and performance admin currently tagged with this performance name.
            </p>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-200 shrink-0" data-testid="event-rename-close">✕</button>
        </div>
        <div className="mb-4">
          <div className="label-tiny mb-1">New name</div>
          <input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && save()}
            className="field-input w-full"
            data-testid="event-rename-input"
          />
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary" data-testid="event-rename-cancel">Cancel</button>
          <button
            onClick={save}
            disabled={!newName.trim() || newName.trim() === eventName || saving}
            className="btn-primary inline-flex items-center gap-1 disabled:opacity-40"
            data-testid="event-rename-save"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Pencil size={12} />}
            Rename
          </button>
        </div>
      </div>
    </div>
  );
}

function EventAdminsModal({ eventName, onClose }) {
  const [admins, setAdmins] = useState(null);
  const [email, setEmail] = useState("");
  const [adding, setAdding] = useState(false);

  const load = () => {
    api.get(`/library-events/${encodeURIComponent(eventName)}/admins`)
      .then((r) => setAdmins(r.data || []))
      .catch((e) => toast.error("Failed to load admins: " + (e?.response?.data?.detail || e.message)));
  };
  useEffect(load, [eventName]);

  const add = async () => {
    const em = email.trim().toLowerCase();
    if (!em) return;
    setAdding(true);
    try {
      await api.post(`/library-events/${encodeURIComponent(eventName)}/admins`, { email: em });
      toast.success(`${em} can now see this performance`);
      setEmail("");
      load();
    } catch (e) {
      toast.error("Failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setAdding(false);
    }
  };

  const remove = async (userId, dispName) => {
    if (!window.confirm(`Remove ${dispName} from this performance?`)) return;
    try {
      await api.delete(`/library-events/${encodeURIComponent(eventName)}/admins/${userId}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error("Failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="event-admins-modal">
      <div className="bg-stone-950 border border-orange-700 max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="label-tiny mb-1">Performance Admins</div>
            <h2 className="font-display uppercase text-2xl font-bold leading-tight">{eventName}</h2>
            <p className="text-xs text-stone-500 mt-1">Users listed here can view this performance's clips even if they're not full admins.</p>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-200" data-testid="event-admins-close">✕</button>
        </div>
        <div className="space-y-3 mb-4">
          {admins === null && <div className="text-stone-500 text-sm inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
          {admins && admins.length === 0 && <div className="text-stone-500 text-sm italic">No performance-specific admins yet.</div>}
          {admins && admins.map((a) => (
            <div key={a.user_id} className="flex items-center justify-between border border-stone-800 px-3 py-2" data-testid={`event-admin-row-${a.user_id}`}>
              <div>
                <div className="text-sm text-stone-200">{a.name || a.email || a.user_id}</div>
                {a.name && a.email && <div className="text-xs text-stone-500">{a.email}</div>}
              </div>
              <button
                onClick={() => remove(a.user_id, a.name || a.email)}
                className="text-stone-500 hover:text-rose-400 p-1"
                title="Remove"
                data-testid={`event-admin-remove-${a.user_id}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com"
            className="field-input flex-1"
            data-testid="event-admin-email"
            onKeyDown={(e) => e.key === "Enter" && add()}
          />
          <button
            onClick={add}
            disabled={adding || !email.trim()}
            className="btn-primary inline-flex items-center gap-1 disabled:opacity-40"
            data-testid="event-admin-add"
          >
            {adding ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
            Add
          </button>
        </div>
      </div>
    </div>
  );
}

